<template>
  <v-dialog
    max-width="720"
    :fullscreen="smAndDown"
    v-model="dialogValue"
    @update:modelValue="$emit('update:modelValue', $event)"
    :persistent="step !== 'choose-mode'"
  >
    <v-card
      class="tw-p-10 sm:tw-p-14 tw-h-full tw-overflow-y-auto tw-relative"
      :class="[smAndDown ? '' : 'tw-rounded-3xl']"
    >
      <AnimatePresence mode="wait">
        <motion.div
          v-if="step === 'choose-mode'"
          :initial="{ opacity: 0 }"
          :animate="{ opacity: 1, height: 'auto' }"
          :transition="{ duration: 0.5 }"
          :exit="{ opacity: 0 }"
        >
          <h1
            id="title"
            class="tw-flex tw-justify-center tw-items-center tw-mb-4"
          >
            <p
              class="tw-text-primary-500 tw-text-2xl tw-font-semibold"
              :class="{ 'tw-text-lg': smAndDown }"
            >
              {{ $t('customTemplateModal.title') }}
            </p>
            <v-btn
              variant="flat"
              class="tw-absolute tw-top-0 tw-right-0 tw-m-4 tw-bg-transparent"
              @click="() => emit('update:modelValue', false)"
              icon="mdi-close"
            />
          </h1>
          <h2
            id="subtitle"
            class="tw-text-sm tw-text-gray-500 tw-mb-8 tw-text-center"
          >
            <p>{{ $t('customTemplateModal.subtitle') }}</p>
          </h2>

          <v-row>
            <v-col cols="12" sm="6">
              <v-card
                :hover="false"
                class="no-hover-overlay tw-group tw-px-10 tw-py-16 tw-rounded-3xl tw-shadow tw-flex tw-justify-center tw-items-center tw-flex-col tw-h-full hover:tw-outline hover:tw-outline-1 hover:tw-outline-primary-500 hover:tw-bg-white"
                @click="
                  handleChangeModalState({
                    stepValue: 'editing-base-model-1',
                    formModeValue: 'editing-base-model',
                    category: 'editedDaai',
                  })
                "
              >
                <v-img
                  src="@/assets/edit_daai_model.svg"
                  :alt="$t('customTemplateModal.options.editModel.title')"
                  height="110"
                  width="130"
                  class="tw-mb-4"
                />
                <div class="tw-max-w-48 tw-text-center">
                  <p
                    class="tw-text-xl tw-font-semibold tw-text-gray-400 group-hover:tw-text-primary-500"
                  >
                    {{ $t('customTemplateModal.options.editModel.title') }}
                  </p>
                  <p
                    class="tw-text-sm tw-text-slate-500/50 group-hover:tw-text-slate-500"
                  >
                    {{
                      $t('customTemplateModal.options.editModel.description')
                    }}
                  </p>
                </div>
              </v-card>
            </v-col>

            <v-col cols="12" sm="6">
              <v-card
                :hover="false"
                class="no-hover-overlay tw-group tw-px-10 tw-py-16 tw-rounded-3xl tw-shadow tw-flex tw-justify-center tw-items-center tw-flex-col tw-h-full hover:tw-outline hover:tw-outline-1 hover:tw-outline-primary-500 hover:tw-bg-white"
                @click="
                  handleChangeModalState({
                    stepValue: 'create-new-model-1',
                    formModeValue: 'create-new-model',
                  })
                "
              >
                <v-img
                  src="@/assets/create_daai_model.svg"
                  :alt="$t('customTemplateModal.options.createModel.title')"
                  height="110"
                  width="130"
                  class="tw-mb-4"
                />
                <div class="tw-max-w-48 tw-text-center">
                  <p
                    class="tw-text-xl tw-font-semibold tw-text-gray-400 group-hover:tw-text-primary-500"
                  >
                    {{ $t('customTemplateModal.options.createModel.title') }}
                  </p>
                  <p
                    class="tw-text-sm tw-text-slate-500/50 group-hover:tw-text-slate-500"
                  >
                    {{
                      $t('customTemplateModal.options.createModel.description')
                    }}
                  </p>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </motion.div>

        <motion.div
          v-if="step !== 'choose-mode'"
          :initial="{ opacity: 0, height: '406px' }"
          :animate="{ opacity: 1, height: 'auto' }"
          :transition="{ duration: 0.5 }"
          :exit="{ opacity: 0, height: '406px' }"
        >
          <h1
            id="title"
            class="tw-flex tw-justify-between tw-items-center tw-mb-4"
          >
            <p
              class="tw-text-primary-500 tw-text-2xl tw-font-semibold tw-items-center"
              :class="{ 'tw-text-lg': smAndDown }"
            >
              <v-icon
                v-if="!choosingTitle"
                :icon="formTitle[formMode].icon"
                size="24"
              />
              {{ modalTitle }}
            </p>
            <v-btn
              variant="flat"
              class="tw-absolute tw-top-0 tw-right-0 tw-m-4 tw-bg-transparent"
              @click="openConfirmDialog"
              icon="mdi-close"
            />
          </h1>

          <h2
            v-if="!choosingTitle && !isEditingGeneratedModel"
            id="subtitle"
            class="tw-text-sm tw-text-gray-500 tw-mb-8"
          >
            <p v-for="text in formSubtitle[formMode]" :key="text">
              {{ text }}
            </p>
          </h2>

          <h2
            v-else-if="choosingTitle"
            id="subtitle"
            class="tw-text-sm tw-text-gray-500 tw-mb-8"
          >
            {{ $t('customTemplateModal.formSubtitles.chooseTitle') }}
          </h2>

          <h2
            v-else-if="isEditingGeneratedModel"
            id="subtitle"
            class="tw-text-sm tw-text-gray-500 tw-mb-8"
          >
            {{ $t('customTemplateModal.formSubtitles.editingGeneratedModel') }}
          </h2>

          <div v-if="step === 'create-new-model-1'" class="tw-mb-8">
            <v-row
              no-gutters
              class="tw-flex tw-justify-around tw-items-center tw-bg-gray-100 tw-rounded-lg tw-p-2"
            >
              <v-col :cols="xs ? 12 : 6">
                <div :class="xs ? 'tw-pr-0' : 'tw-pr-2'">
                  <v-btn
                    :color="createMode === 'manual' ? 'primary' : 'gray'"
                    :variant="createMode === 'manual' ? 'outlined' : 'text'"
                    size="large"
                    @click="handleCreateModeChange('manual')"
                    class="tw-w-full tw-rounded-md"
                    :class="[
                      createMode === 'manual'
                        ? 'tw-bg-white tw-text-primary-500 tw-font-semibold'
                        : 'tw-bg-transparent tw-text-gray-500 tw-font-normal',
                    ]"
                    :ripple="false"
                  >
                    <v-icon icon="mdi-fountain-pen" class="tw-mr-2" />
                    <p>
                      {{ $t('customTemplateModal.createMode.manual.title') }}
                    </p>
                  </v-btn>
                </div>
              </v-col>

              <v-col :cols="xs ? 12 : 6">
                <div :class="xs ? 'tw-pt-2' : 'tw-pt-0'">
                  <v-btn
                    :color="createMode === 'ai' ? 'primary' : 'gray'"
                    :variant="createMode === 'ai' ? 'outlined' : 'text'"
                    size="large"
                    @click="handleCreateModeChange('ai')"
                    class="tw-w-full tw-rounded-md"
                    :class="[
                      createMode === 'ai'
                        ? 'tw-bg-white tw-text-primary-500 tw-font-semibold'
                        : 'tw-bg-transparent tw-text-gray-500 tw-font-normal',
                    ]"
                    :ripple="false"
                  >
                    <AnimatePresence mode="popLayout">
                      <motion.div
                        v-if="createMode === 'manual'"
                        :initial="{ opacity: 0 }"
                        :animate="{ opacity: 1 }"
                        :transition="{ duration: 0.3 }"
                        :exit="{ opacity: 0 }"
                      >
                        <v-img
                          src="@/assets/daai-sad.svg"
                          :alt="$t('customTemplateModal.createMode.ai.title')"
                          width="40"
                          class="tw-mr-2"
                        />
                      </motion.div>
                      <motion.div
                        v-else
                        :initial="{ opacity: 0 }"
                        :animate="{ opacity: 1 }"
                        :transition="{ duration: 0.3 }"
                        :exit="{ opacity: 0 }"
                      >
                        <v-img
                          src="@/assets/daai-happy.svg"
                          :alt="$t('customTemplateModal.createMode.ai.title')"
                          width="40"
                          class="tw-mr-2"
                        />
                      </motion.div>
                    </AnimatePresence>
                    <p>{{ $t('customTemplateModal.createMode.ai.title') }}</p>
                  </v-btn>
                </div>
              </v-col>
            </v-row>
          </div>

          <Form
            v-if="formMode === 'editing-base-model'"
            ref="editingBaseFormRef"
            :validation-schema="customTemplateFormSchema"
            :initial-values="initialValues"
            @submit="handleEditBaseSubmit"
            v-slot="{ meta, values }"
            class="tw-space-y-4"
          >
            <div
              v-if="formMode === 'editing-base-model'"
              v-show="step === 'editing-base-model-1'"
            >
              <Field
                name="specialtyTemplate"
                v-slot="{ field, errorMessage, handleChange, value }"
              >
                <v-select
                  v-bind="field"
                  :model-value="value"
                  @update:model-value="
                    (newValue) => {
                      handleChange(newValue)
                      selectedSpecialty = newValue
                    }
                  "
                  id="specialtyTemplate"
                  :items="specialtyTemplateOptions"
                  item-title="title"
                  item-value="value"
                  :label="$t('customTemplateModal.fields.baseModel')"
                  color="primary"
                  variant="outlined"
                  :error-messages="errorMessage"
                  rounded="lg"
                  :loading="isSpecialtyModelsLoading"
                  hide-details
                  class="tw-mb-8"
                />
              </Field>
            </div>

            <div
              v-if="isSpecialtyMarkdownFetching"
              class="tw-h-[340px] tw-flex tw-justify-center tw-items-center"
            >
              <DaaiLoading />
            </div>

            <div
              v-if="values.specialtyTemplate && !isSpecialtyMarkdownFetching"
              v-show="step === 'editing-base-model-1'"
            >
              <Field
                name="templatePrompt"
                v-slot="{
                  field,
                  errorMessage,
                  handleChange,
                  value,
                  handleBlur,
                }"
              >
                <h2 id="subtitle" class="tw-text-sm tw-text-gray-500 tw-mb-2">
                  {{
                    $t(
                      'customTemplateModal.formSubtitles.editingBaseModel.text2',
                    )
                  }}
                </h2>
                <MarkdownEditor
                  v-bind="field"
                  :model-value="value"
                  @update:model-value="handleChange"
                  @blur="handleBlur"
                  id="templatePromptEditor"
                  :placeholder="$t('customTemplateModal.fields.templatePrompt')"
                  :error-messages="errorMessage"
                  :disabled="isSpecialtyMarkdownFetching"
                  class="tw-mb-8"
                />
              </Field>
            </div>

            <div v-show="step === 'editing-base-model-2'">
              <Field
                name="templateTitle"
                v-slot="{
                  field,
                  errorMessage,
                  handleChange,
                  value,
                  handleBlur,
                }"
              >
                <v-text-field
                  v-bind="field"
                  :model-value="value"
                  @update:model-value="handleChange"
                  @blur="handleBlur"
                  id="templateTitle"
                  :label="$t('customTemplateModal.fields.templateTitle')"
                  :placeholder="
                    $t('customTemplateModal.fields.templateTitlePlaceholder', {
                      modelName: specialtyTemplateOptions.find(
                        (option) => option.value === values.specialtyTemplate,
                      )?.title,
                    })
                  "
                  variant="outlined"
                  :error-messages="errorMessage"
                  rounded="lg"
                />
              </Field>
            </div>

            <div class="tw-flex tw-justify-between tw-items-center">
              <v-btn
                color="primary"
                variant="outlined"
                rounded="xl"
                size="large"
                @click="handleBackButton"
              >
                {{ $t('customTemplateModal.buttons.back') }}
              </v-btn>

              <v-btn
                v-if="step === 'editing-base-model-1'"
                color="primary"
                variant="flat"
                rounded="xl"
                size="large"
                type="submit"
                :disabled="
                  (!values.specialtyTemplate && !values.templatePrompt) ||
                  isSpecialtyMarkdownFetching
                "
                @click.prevent="step = 'editing-base-model-2'"
                class="tw-bg-gradient-to-r tw-from-primary-600 tw-to-primary-400"
              >
                {{ $t('customTemplateModal.buttons.saveCustomization') }}
              </v-btn>

              <v-btn
                v-if="step === 'editing-base-model-2'"
                color="primary"
                variant="flat"
                rounded="xl"
                size="large"
                type="submit"
                :loading="isCreatingCustomTemplate"
                :disabled="!meta.valid"
                class="tw-bg-gradient-to-r tw-from-primary-600 tw-to-primary-400"
              >
                <v-icon icon="mdi-creation-outline" class="tw-mr-2" />
                {{
                  cameFromModelApplyer
                    ? $t('customTemplateModal.buttons.saveAndApply')
                    : $t('customTemplateModal.buttons.saveNewModel')
                }}
              </v-btn>
            </div>
          </Form>

          <Form
            v-if="formMode === 'create-new-model'"
            ref="createNewFormRef"
            v-show="
              step === 'create-new-model-1' ||
              step === 'create-new-model-2' ||
              step === 'create-new-model-3'
            "
            :validation-schema="customTemplateFormSchemaModeCreate"
            :initial-values="createInitialValues"
            @submit="handleCreateNewModelSubmit"
            v-slot="{ meta, values, resetForm, setFieldValue, validateField }"
          >
            <div class="tw-mb-8" v-show="step === 'create-new-model-1'">
              <Field
                name="templatePrompt"
                v-slot="{ field, handleChange, value, setValue }"
              >
                <p class="tw-text-sm tw-text-gray-500 tw-mb-6">
                  {{
                    createMode === 'manual'
                      ? $t('customTemplateModal.createMode.manual.description')
                      : $t('customTemplateModal.createMode.ai.description')
                  }}
                </p>

                <!-- // sugestions -->

                <AnimatePresence mode="wait">
                  <motion.div
                    v-if="createMode === 'ai'"
                    :initial="{ height: 0, scaleY: 0, opacity: 0 }"
                    :animate="{
                      height: 'auto',
                      scaleY: 1,
                      opacity: 1,
                    }"
                    :transition="{ duration: 0.5 }"
                    :exit="{ height: 0, scaleY: 0, opacity: 0 }"
                  >
                    <div
                      class="tw-flex tw-flex-wrap tw-justify-stretch tw-text-sm tw-mb-2 tw-gap-2"
                    >
                      <p class="tw-text-xs tw-w-full tw-text-gray-700 tw-mb-2">
                        {{ $t('customTemplateModal.suggestions.title') }}
                      </p>
                      <div
                        v-for="suggestion in templateSuggestions"
                        :key="suggestion"
                        class="tw-grow"
                      >
                        <v-btn
                          :key="suggestion"
                          variant="outlined"
                          size="small"
                          class="tw-tracking-normal tw-w-full tw-text-xs tw-text-gray-700 tw-px-2 tw-py-1 tw-rounded-xl tw-border-blue-100 tw-normal-case"
                          :ripple="false"
                          @click="setValue(suggestion)"
                        >
                          {{ suggestion }}
                        </v-btn>
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>
                <MarkdownEditor
                  v-bind="field"
                  :model-value="value"
                  @update:model-value="handleChange"
                  :placeholder="
                    createMode === 'manual'
                      ? $t('customTemplateModal.fields.manualPrompt')
                      : $t('customTemplateModal.fields.aiPrompt')
                  "
                  :disabled="isGeneratingMarkdown"
                  :show-tips="
                    createMode !== 'ai' || step === 'create-new-model-3'
                  "
                />
              </Field>
            </div>

            <div v-show="step === 'create-new-model-2'" class="tw-mb-8">
              <Field
                name="templateTitle"
                v-slot="{
                  field,
                  errorMessage,
                  handleChange,
                  value,
                  handleBlur,
                }"
              >
                <v-text-field
                  v-bind="field"
                  :model-value="value"
                  @update:model-value="handleChange"
                  @blur="handleBlur"
                  id="templateTitle"
                  :label="$t('customTemplateModal.fields.createModelTitle')"
                  variant="outlined"
                  :error-messages="errorMessage"
                  rounded="lg"
                />
              </Field>
            </div>

            <div
              v-show="step === 'create-new-model-3'"
              class="tw-mb-8 tw-relative"
            >
              <div
                v-if="isGeneratingMarkdown"
                class="tw-h-[340px] tw-bg-white tw-flex tw-justify-center tw-items-center tw-absolute tw-top-0 tw-left-0 tw-right-0 tw-bottom-0"
              >
                <DaaiLoading />
              </div>
              <Field
                name="templatePrompt"
                v-slot="{
                  field,
                  errorMessage,
                  handleChange,
                  value,
                  handleBlur,
                }"
              >
                <MarkdownEditor
                  v-bind="field"
                  :model-value="value"
                  @update:model-value="handleChange"
                  @blur="handleBlur"
                  id="templatePromptEditor"
                  :placeholder="
                    $t('customTemplateModal.fields.editGeneratedModel')
                  "
                  :error-messages="errorMessage"
                  class="tw-mb-8"
                  :disabled="isGeneratingMarkdown"
                />
              </Field>
            </div>

            <div class="tw-flex tw-justify-between tw-items-center">
              <v-btn
                color="primary"
                variant="outlined"
                rounded="xl"
                size="large"
                @click="() => handleBackButton(resetForm)"
                :disabled="isGeneratingMarkdown"
              >
                {{ $t('customTemplateModal.buttons.back') }}
              </v-btn>

              <v-btn
                v-if="step === 'create-new-model-1'"
                color="primary"
                variant="flat"
                rounded="xl"
                size="large"
                @click.prevent="
                  handleGenerateModel(values, setFieldValue, validateField)
                "
                :disabled="!values.templatePrompt"
                :loading="isGeneratingMarkdown"
                :class="[
                  createMode === 'ai'
                    ? 'tw-bg-gradient-to-r tw-from-primary-600 tw-to-primary-400'
                    : '',
                ]"
              >
                <AnimatePresence mode="wait">
                  <motion.div
                    v-if="createMode === 'ai'"
                    :initial="{ opacity: 0, x: 10, width: 0 }"
                    :animate="{ opacity: 1, x: 0, width: 'auto' }"
                    :transition="{ duration: 0.5 }"
                    :exit="{ opacity: 0, x: 10, width: 0 }"
                  >
                    <v-icon icon="mdi-creation-outline" class="tw-mr-2" />
                  </motion.div>
                </AnimatePresence>
                {{
                  createMode === 'manual'
                    ? $t('customTemplateModal.buttons.saveModel')
                    : $t('customTemplateModal.buttons.generateModel')
                }}
              </v-btn>

              <v-btn
                v-if="step === 'create-new-model-2'"
                color="primary"
                variant="flat"
                rounded="xl"
                size="large"
                type="submit"
                :loading="isSubmitting"
                :disabled="!meta.valid"
                class="tw-bg-gradient-to-r tw-from-primary-600 tw-to-primary-400"
              >
                <v-icon icon="mdi-creation-outline" class="tw-mr-2" />
                {{
                  cameFromModelApplyer
                    ? $t('customTemplateModal.buttons.saveAndApply')
                    : $t('customTemplateModal.buttons.saveNewModel')
                }}
              </v-btn>

              <v-btn
                v-if="step === 'create-new-model-3'"
                color="primary"
                variant="flat"
                rounded="xl"
                size="large"
                @click.prevent="step = 'create-new-model-2'"
                class="tw-bg-gradient-to-r tw-from-primary-600 tw-to-primary-400"
                :disabled="isGeneratingMarkdown || !values.templatePrompt"
              >
                <v-icon icon="mdi-creation-outline" class="tw-mr-2" />
                {{ $t('customTemplateModal.buttons.saveCustomization') }}
              </v-btn>
            </div>
          </Form>

          <div v-if="formMode === 'edit-mode'">
            <MarkdownEditor
              class="tw-mb-8"
              :model-value="editingContent"
              @update:model-value="(value) => (editingContent = value)"
              :disabled="isGeneratingMarkdown"
            />

            <div class="tw-flex tw-justify-between tw-items-center">
              <v-btn
                color="primary"
                variant="outlined"
                rounded="xl"
                size="large"
                @click="openConfirmDialog"
              >
                {{ $t('customTemplateModal.buttons.back') }}
              </v-btn>

              <v-btn
                color="primary"
                variant="flat"
                rounded="xl"
                size="large"
                @click.prevent="handleCreateNewModelSubmit"
                :loading="isUpdatingCustomPrompt"
                :disabled="isUpdatingCustomPrompt"
                class="tw-bg-gradient-to-r tw-from-primary-600 tw-to-primary-400"
              >
                {{ $t('customTemplateModal.buttons.saveEdit') }}
              </v-btn>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </v-card>

    <ConfirmDialog
      v-model="showConfirmDialog"
      icon="mdi-alert-outline"
      iconBgColor="tw-bg-danger-500/[.1]"
      base-color="red"
      :title="$t('customTemplateModal.confirmDialog.title')"
      :message="$t('customTemplateModal.confirmDialog.message')"
      :cancel-button-text="$t('customTemplateModal.confirmDialog.cancelButton')"
      :confirm-button-text="
        $t('customTemplateModal.confirmDialog.confirmButton')
      "
      @cancel="showConfirmDialog = false"
      @confirm="handleConfirmExit"
      :max-width="'420'"
    />
  </v-dialog>
</template>

<script setup>
import { useDisplay } from 'vuetify'
import { Form, Field } from 'vee-validate'
import { useToast } from 'vue-toastification'
import { motion } from 'motion-v'
import { useI18n } from '@/composables/useI18n'
import { useGetSpecialtyModels } from '@/api/consultations/queries'
import { useGetSpecialtyMarkdown } from '@/api/customizations/queries'
import { useRequestConsultationReport } from '@/api/consultations/mutations'
import {
  useUpdateCustomPrompt,
  useCreateCustomPrompt,
  useGenerateCustomMarkdown,
} from '@/api/customizations/mutations'
import {
  createCustomTemplateFormSchema,
  createCustomTemplateFormSchemaModeCreate,
} from '@/composables/schemas/custom-template-form'
import { useUserStore } from '@/stores/user'
import { useCustomizationStore } from '@/stores/customization'
import { useConsultationStore } from '@/stores/consultation'
import { useUserMetricsStore } from '@/stores/userMetrics'
import { useModalsStore } from '@/stores/modals'
import { useAppStore } from '@/stores/app'
import { usePaywallGuard } from '@/composables/usePaywallGuard'

const emit = defineEmits(['update:modelValue', 'profile-completed'])

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
})

const { smAndDown, xs } = useDisplay()
const { t } = useI18n()
const toast = useToast()
const customizationStore = useCustomizationStore()
const consultationStore = useConsultationStore()
const userStore = useUserStore()
const userMetricsStore = useUserMetricsStore()
const modalsStore = useModalsStore()
const appStore = useAppStore()
const {
  step,
  formMode,
  createMode,
  isEditing,
  modelToEdit,
  cameFromModelApplyer,
} = storeToRefs(customizationStore)
const { refetchUserMetrics } = userMetricsStore
const { isProUser } = storeToRefs(userStore)
const {
  blockIfCustomPromptCategoryLimitReached,
  blockIfCustomPromptCreationNotAllowed,
} = usePaywallGuard()

const getCreationCategory = () => {
  if (formMode.value === 'editing-base-model') return 'editedDaai'
  if (createMode.value === 'manual') return 'freeText'
  return 'aiGenerated'
}

const blockIfCategoryLimitReached = (category = getCreationCategory()) => {
  if (isProUser.value || isEditing.value) return false
  return blockIfCustomPromptCategoryLimitReached(category)
}

const closeModalAndShowLimit = () => {
  emit('update:modelValue', false)
}

const handleCreateModeChange = (mode) => {
  const category = mode === 'manual' ? 'freeText' : 'aiGenerated'
  if (blockIfCategoryLimitReached(category)) {
    closeModalAndShowLimit()
    return
  }
  createMode.value = mode
}

const isSubmitting = ref(false)
const editingContent = ref('')
const editingBaseFormRef = ref(null)
const createNewFormRef = ref(null)

const customTemplateFormSchema = createCustomTemplateFormSchema(t)
const customTemplateFormSchemaModeCreate =
  createCustomTemplateFormSchemaModeCreate(t)

const formTitle = computed(() => ({
  'editing-base-model': {
    icon: 'mdi-pencil-outline',
    text: t('customTemplateModal.formTitles.editingBaseModel'),
  },
  'create-new-model': {
    icon: 'mdi-lightbulb-outline',
    text: t('customTemplateModal.formTitles.createNewModel'),
  },
  'edit-mode': {
    icon: 'mdi-creation-outline',
    text: t('customTemplateModal.formTitles.editMode'),
  },
}))

const formSubtitle = computed(() => ({
  'editing-base-model': {
    text1: t('customTemplateModal.formSubtitles.editingBaseModel.text1'),
    text2: t('customTemplateModal.formSubtitles.editingBaseModel.text2'),
  },
  'create-new-model': {
    text1: t('customTemplateModal.formSubtitles.createNewModel.text1'),
  },
  'edit-mode': {
    text1: t('customTemplateModal.formSubtitles.editMode.text1'),
  },
}))

const modalTitle = computed(() => {
  return choosingTitle.value
    ? t('customTemplateModal.formTitles.chooseTitle')
    : isEditingGeneratedModel.value
    ? t('customTemplateModal.formTitles.editMode')
    : formTitle.value[formMode.value].text
})

const templateSuggestions = computed(() => [
  t('customTemplateModal.suggestions.items.useBulletPoints'),
  t('customTemplateModal.suggestions.items.returnConsultationModel'),
  t('customTemplateModal.suggestions.items.evolutionRecord'),
  t('customTemplateModal.suggestions.items.firstPersonRecord'),
  t('customTemplateModal.suggestions.items.noGender'),
  t('customTemplateModal.suggestions.items.freeTextRecord'),
])

watch(
  () => props.modelValue,
  (isOpen) => {
    if (!isOpen) return
    refetchUserMetrics()
    appStore.refetchCustomPromptList()
  },
)

const { data: specialtyModels, isLoading: isSpecialtyModelsLoading } =
  useGetSpecialtyModels()

const {
  mutateAsync: createCustomTemplate,
  isPending: isCreatingCustomTemplate,
} = useCreateCustomPrompt()

const { mutateAsync: updateCustomPrompt, isPending: isUpdatingCustomPrompt } =
  useUpdateCustomPrompt()

const { mutateAsync: generateCustomMarkdown, isPending: isGeneratingMarkdown } =
  useGenerateCustomMarkdown()

const { mutateAsync: requestConsultationReport } =
  useRequestConsultationReport()

const selectedSpecialty = ref(null)

const { data: specialtyMarkdown, isFetching: isSpecialtyMarkdownFetching } =
  useGetSpecialtyMarkdown({ specialty: selectedSpecialty })

const specialtyTemplateOptions = computed(() => {
  return Object.entries(specialtyModels.value?.specialties ?? {})
    .map(([specialty, { title }]) => ({
      title,
      value: specialty,
    }))
    .sort((a, b) => a.title.localeCompare(b.title))
})

const initialValues = computed(() => ({
  specialtyTemplate: null,
  templateTitle: '',
  templatePrompt: '',
}))

const createInitialValues = computed(() => ({
  templatePrompt: '',
  templateTitle: '',
}))

const showConfirmDialog = ref(false)

const choosingTitle = computed(() => {
  return (
    step.value === 'editing-base-model-2' || step.value === 'create-new-model-2'
  )
})

const isEditingGeneratedModel = computed(() => {
  return step.value === 'create-new-model-3'
})

const handleChangeModalState = ({ stepValue, formModeValue, category }) => {
  if (category && blockIfCategoryLimitReached(category)) {
    closeModalAndShowLimit()
    return
  }

  if (formModeValue === 'create-new-model' && blockIfCustomPromptCreationNotAllowed()) {
    closeModalAndShowLimit()
    return
  }

  if (stepValue) step.value = stepValue
  if (formModeValue) formMode.value = formModeValue
}

function openConfirmDialog() {
  showConfirmDialog.value = true
}

function handleConfirmExit() {
  showConfirmDialog.value = false
  emit('update:modelValue', false)
}

const handleApplyNewlyCreatedModel = async (modelData) => {
  try {
    const requestData = {
      consultationId: consultationStore.consultationId,
      customPromptId: modelData.id,
    }
    await requestConsultationReport(requestData)
    toast.success(t('modelApplyerModal.modelAppliedSuccess'))
  } catch (error) {
    console.error('Erro ao aplicar modelo:', error)
    throw error
  }
}

// Função para pré-selecionar o custom prompt criado
const preselectCreatedCustomPrompt = (result, values) => {
  if (result?.id) {
    const newSelectedItem = {
      title: values.templateTitle,
      value: result.specialty || 'generic', // fallback para generic se não tiver specialty
      id: result.id,
      content: values.templatePrompt,
      specialty: result.specialty || 'generic',
      isCustom: true,
      isActive: true,
    }

    // Atualiza o selectedModelSpecialty na store
    appStore.setSelectedModelSpecialty(newSelectedItem)
  }
}

const handleEditBaseSubmit = async (values) => {
  if (blockIfCategoryLimitReached('editedDaai')) {
    closeModalAndShowLimit()
    return
  }

  isSubmitting.value = true
  try {
    // Simula chamada de API
    const result = await createCustomTemplate({
      specialty: values.specialtyTemplate,
      content: values.templatePrompt,
      title: values.templateTitle,
      creationMethod: 'EDITED_DAAI',
    })
    refetchUserMetrics()
    toast.success(t('customTemplateModal.messages.modelCreated'))

    // Pré-selecionar o custom prompt criado
    preselectCreatedCustomPrompt(result, values)

    // Se veio do modal de aplicação de modelo, aplicar diretamente
    if (cameFromModelApplyer.value) {
      if (!result?.id) {
        console.error('API não retornou ID do modelo criado:', result)
        toast.error(t('customTemplateModal.messages.errorCreatingModel'))
        return
      }

      const modelData = {
        id: result.id,
        title: values.templateTitle,
        isCustom: true,
      }

      // Aplicar o modelo diretamente
      try {
        await handleApplyNewlyCreatedModel(modelData)
        emit('update:modelValue', false)
      } catch (error) {
        console.error('Erro ao aplicar modelo:', error)
        toast.error(t('modelApplyerModal.modelAppliedError'))
      }
    } else {
      emit('profile-completed', values)
      emit('update:modelValue', false)
    }
  } catch (error) {
    const msg = String(error?.message || '')
    const isLimitReached = /já criou|already created|limit|limite|apenas um|only one|custom prompt|modelo customizado|already has/i.test(msg)
    toast.error(
      isLimitReached
        ? t('customTemplateModal.messages.limitReachedPerCategory')
        : t('customTemplateModal.messages.errorCreatingModel'),
    )
    console.error(error)
  } finally {
    isSubmitting.value = false
  }
}

const handleGenerateModel = async (values, setFieldValue, validateField) => {
  if (createMode.value === 'manual') {
    step.value = 'create-new-model-2'
    return
  }

  try {
    step.value = 'create-new-model-3'
    const result = await generateCustomMarkdown({
      instructions: values.templatePrompt,
    })

    setFieldValue('templatePrompt', result.markdown_template)
    validateField('templatePrompt')
  } catch (error) {
    toast.error(t('customTemplateModal.messages.errorGeneratingModel'))
    console.error(error)
  }
}

const handleCreateNewModelSubmit = async (values) => {
  if (!isEditing.value && blockIfCategoryLimitReached()) {
    closeModalAndShowLimit()
    return
  }

  isSubmitting.value = true
  try {
    if (isEditing.value && modelToEdit.value) {
      await updateCustomPrompt({
        id: modelToEdit.value.id,
        content: editingContent.value,
        from: 'custom-prompts-list',
      })
      toast.success(t('customTemplateModal.messages.modelUpdated'))
      emit('profile-completed', values)
      emit('update:modelValue', false)
    } else {
      if (!values) {
        throw new Error('Values not found')
      }

      const result = await createCustomTemplate({
        content: values.templatePrompt,
        title: values.templateTitle,
        creationMethod:
          createMode.value === 'manual' ? 'FREE_TEXT' : 'AI_GENERATED',
      })

      refetchUserMetrics()
      toast.success(t('customTemplateModal.messages.modelCreated'))

      // Pré-selecionar o custom prompt criado
      preselectCreatedCustomPrompt(result, values)

      // Se veio do modal de aplicação de modelo, aplicar diretamente
      if (cameFromModelApplyer.value) {
        if (!result?.id) {
          console.error('API não retornou ID do modelo criado:', result)
          toast.error(t('customTemplateModal.messages.errorCreatingModel'))
          return
        }

        const modelData = {
          id: result.id,
          title: values.templateTitle,
          isCustom: true,
        }

        // Aplicar o modelo diretamente
        try {
          await handleApplyNewlyCreatedModel(modelData)
          emit('update:modelValue', false)
        } catch (error) {
          console.error('Erro ao aplicar modelo:', error)
          toast.error(t('modelApplyerModal.modelAppliedError'))
        }
      } else {
        emit('profile-completed', values)
        emit('update:modelValue', false)
      }
    }
  } catch (error) {
    const msg = String(error?.message || '')
    const isLimitReached = /já criou|already created|limit|limite|apenas um|only one|custom prompt|modelo customizado|already has/i.test(msg)
    toast.error(
      isLimitReached
        ? t('customTemplateModal.messages.limitReachedPerCategory')
        : t('customTemplateModal.messages.errorCreatingModel'),
    )
    console.error(error)
  } finally {
    isSubmitting.value = false
  }
}

watch([selectedSpecialty, specialtyMarkdown], ([newSpecialty, newMarkdown]) => {
  if (newSpecialty && newMarkdown) {
    nextTick(() => {
      if (editingBaseFormRef.value && editingBaseFormRef.value.setFieldValue) {
        editingBaseFormRef.value.setFieldValue(
          'templatePrompt',
          newMarkdown.daai,
        )
      }
    })
  }
})

const dialogValue = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
})

// Resetar a especialidade selecionada quando o modal for fechado
watch(
  () => props.modelValue,
  (value) => {
    if (!value) {
      step.value = 'choose-mode'
      selectedSpecialty.value = null
      createMode.value = 'manual' // Resetar o modo de criação
      formMode.value = null
      isEditing.value = false
      modelToEdit.value = null
      // Resetar flag de origem do modal
      cameFromModelApplyer.value = false
    }
  },
)

watch(
  () => isEditing.value,
  () => {
    if (modelToEdit.value) {
      formMode.value = 'edit-mode'
      step.value = 'edit-mode-1'
      editingContent.value = modelToEdit.value.content
      selectedSpecialty.value = modelToEdit.value.specialty
    }
  },
)

const handleBackButton = (resetForm) => {
  const stepsMap = {
    'editing-base-model-1': 'choose-mode',
    'editing-base-model-2': 'editing-base-model-1',
    'create-new-model-1': 'choose-mode',
    'create-new-model-2': 'create-new-model-1',
    'create-new-model-3': 'create-new-model-1',
  }
  step.value = stepsMap?.[step.value] ?? 'choose-mode'
  if (step.value === 'create-new-model-1') {
    isSubmitting.value = false
    resetForm()
  }
}
</script>

<style scoped lang="scss">
:deep() {
  .v-field--focused {
    color: #00b6ce !important;
  }
  .v-input__details {
    padding-left: 0 !important;
  }
  .no-hover-overlay .v-card__overlay {
    opacity: 0;
  }
}

label {
  font-size: 15px;
  font-weight: 500;
}

.card-shadow {
  box-shadow: 0px -62px 22px -48px rgba(var(--v-theme-primary), 0.3) !important;
}
</style>
